/**
 * Formats the menu items recursively with submenus.
 *
 * @param {Array} menuItems - The array of menu items to format.
 * @param {number} [layer=1] - The current layer of the menu.
 * @returns {Array} The formatted menu items.
 */
export const formatMenu = (menuItems, layer = 1) => {
  return menuItems.map((item, index) => {
    const {id, name, webURI, mWebShow, subMenu, type} = item;

    const viewAllItem =
      subMenu && webURI
        ? [
            {
              id: `${id}-all`,
              name: mWebShow ? mWebShow : `View All ${name}`,
              webURI,
            },
          ]
        : [];

    return subMenu
      ? {
          id,
          label: name,
          href: webURI || null,
          type: type || null,
          subMenu: formatMenu([...subMenu, ...viewAllItem], layer + 1),
          layer,
          index,
        }
      : {
          id,
          label: name,
          href: webURI || null,
          layer,
          index,
        };
  });
};

/**
 * Recursively pulls menu items by level from a nested menu structure.
 *
 * @param {Array} menuItems - The array of menu items.
 * @param {Array} menuByLevel - The array to store the menu items by level.
 * @returns {Array} - The menu items pulled by level.
 */
export const pullMenuByLevel = (menuItems, menuByLevel = []) => {
  menuItems.forEach((menuItem) => {
    if (menuItem.items || menuItem.subMenu) {
      const menuLevel = menuItem.items || menuItem.subMenu;
      menuLevel.forEach((level) => {
        if (!level.parent) {
          level.parent = menuItem.id;
          level.parentLabel = menuItem.label;
        }
      });
      menuByLevel.push(menuLevel);

      return pullMenuByLevel(menuLevel, menuByLevel);
    }
  });

  return menuByLevel;
};

/**
 * Retrieves the ancestry of an item in the full menu.
 *
 * @param {Object} item - The item for which to retrieve the ancestry.
 * @param {Object} fullMenu - The full menu object containing all items.
 * @returns {Array} - An array representing the ancestry of the item.
 */
export const getItemAncestry = (item, fullMenu) => {
  const result = [item];

  Object.values(fullMenu).forEach(({subMenu = [], ...parent}) => {
    const parentItem = subMenu.find(({id}) => id === item.parent);
    if (parentItem) {
      delete parentItem.subMenu;
      delete parent.subMenu;
      result.push(parentItem, parent);
    }
  });

  return result;
};
